import * as THREE from "three";
import { ExternalImageMetadata, SurveyResource } from "@/swagger";
import type { ExternalImage } from "./external-image";
import { ExternalImageMagnifierRenderer } from "./external-image-magnifier-renderer";
import { Tools } from "./tools";

declare const Potree: any;

const TEXTURE_CACHE_BYTES = 256 * 1024 * 1024;
const TEXTURE_CACHE_COUNT = 3;
const MAX_TEXTURE_LOADS = 2;
const IMAGE_SPHERE_RADIUS = 500;
const GLIDE_MIN_DURATION = 650;
const GLIDE_MAX_DURATION = 1100;
const SPATIAL_GLIDE_MIN_DURATION = 1000;
const SPATIAL_GLIDE_MAX_DURATION = 1800;
const PANORAMA_HANDOFF_FRACTION = 0.22;

const OVERLAY_RENDER_ORDER = 1000002;
const MARKER_DEFAULT_COLOR = 0xffffff;
const MARKER_HOVER_COLOR = 0xffc800;
const MARKER_DEFAULT_OPACITY = 128 / 255;
// Metadata rotation describes a camera frame with X forward, Y left, Z down.
// The panorama texture V axis is flipped at load time so image-space up stays up.
const EQUIRECTANGULAR_TO_CAMERA = new THREE.Quaternion().setFromRotationMatrix(
	new THREE.Matrix4().makeBasis(
		new THREE.Vector3(1, 0, 0),
		new THREE.Vector3(0, 0, -1),
		new THREE.Vector3(0, 1, 0),
	),
);

interface ExternalImageManagerOptions {
	viewer: any;
	measurementsGroup: THREE.Group;
	unifiedLineMeasurementsGroup: THREE.Group;
	annotationsGroup: THREE.Group;
	mousePosition: THREE.Vector2;
	lastValidIntersectionPoint: THREE.Vector3;
	getCurrentTool: () => Tools;
	getOverlayTargets: () => any[];
	isCadPointMoveActive: () => boolean;
}

export class ExternalImageManager {
	private readonly markersGroup = new THREE.Group();
	private readonly imageViewGroup = new THREE.Group();
	private readonly externalImageMarkers: THREE.Mesh[] = [];
	private readonly markerNearestDistances = new WeakMap<THREE.Mesh, number>();
	private readonly markerGeometry = new THREE.SphereGeometry(1, 24, 16);
	private readonly imageSphereGeometry = new THREE.SphereGeometry(IMAGE_SPHERE_RADIUS, 60, 40);
	private readonly imageTextureLoader = new THREE.TextureLoader();
	private readonly textureCache = new Map<string, { texture: THREE.Texture; bytes: number }>();
	private readonly textureLoads = new Map<string, Promise<THREE.Texture>>();
	private textureGeneration = 0;
	private runningTextureLoads = 0;
	private readonly textureQueue: { url: string; start: () => void; cancel: () => void }[] = [];
	private readonly prefetchUrls = new Set<string>();
	private navigationDirection: -1 | 1 = 1;
	private prefetchTimer: ReturnType<typeof setTimeout> | undefined;
	private readonly imageRotationCache = new WeakMap<ExternalImage, THREE.Quaternion>();
	private readonly markerRaycaster = new THREE.Raycaster();
	private readonly markerRaycastMouse = new THREE.Vector2();
	private readonly markerWorldPosition = new THREE.Vector3();
	public readonly magnifier: ExternalImageMagnifierRenderer;
	private imageSphere: THREE.Mesh | null = null;
	private pendingImageSphere: THREE.Mesh | null = null;
	private transition: {
		outgoing: THREE.Mesh;
		progress: number;
		revealScene: boolean;
		frame: number;
		finished: Promise<void>;
		resolve: () => void;
	} | null = null;
	private interactionElement: HTMLElement | null = null;
	private externalImageOpacity = 1;
	private hoveredMarker: THREE.Mesh | null = null;
	private hoverLabel: any;
	private markersVisible = true;
	private viewState: {
		view: any;
		fov: number;
		inputEnabled: boolean;
	} | null = null;
	private activeImage: ExternalImage | null = null;
	private requestedImage: ExternalImage | null = null;

	public constructor(private readonly options: ExternalImageManagerOptions) {
		Object.assign(this.markersGroup, { name: "ExternalImagesGroup", renderOrder: 10000 });
		Object.assign(this.imageViewGroup, { name: "ExternalImageGroup", renderOrder: 1000 });

		const scene = this.options.viewer.scene.scene;
		scene.add(this.imageViewGroup);
		scene.add(this.markersGroup);
		this.magnifier = new ExternalImageMagnifierRenderer({
			viewer: this.options.viewer,
			hiddenGroups: [
				this.imageViewGroup,
				this.markersGroup,
				this.options.measurementsGroup,
				this.options.unifiedLineMeasurementsGroup,
			],
			mousePosition: this.options.mousePosition,
			snappedPoint: this.options.lastValidIntersectionPoint,
			getCurrentTool: this.options.getCurrentTool,
			isImageViewActive: () => this.viewState != null,
			isCadPointMoveActive: this.options.isCadPointMoveActive,
		});

		this.hoverLabel = new Potree.TextSprite();
		this.hoverLabel.setTextColor({r: 255, g: 255, b: 255, a: 1.0});
		this.hoverLabel.setBorderColor({r: 0, g: 0, b: 0, a: 1.0});
		this.hoverLabel.setBackgroundColor({r: 0, g: 0, b: 0, a: 0.8});
		Object.assign(this.hoverLabel, { fontsize: 18, visible: false, renderOrder: 1000000 });
		Object.assign(this.hoverLabel.material, { depthTest: false, transparent: true, depthWrite: false, opacity: 1 });
		this.markersGroup.add(this.hoverLabel);
	}

	public get markers(): THREE.Mesh[] { return this.externalImageMarkers; }
	public get pickableMarkers(): THREE.Mesh[] {
		return this.markersGroup.visible ? this.externalImageMarkers.filter((marker) => marker.visible) : [];
	}
	public get isActive(): boolean { return this.viewState != null; }
	public get isReady(): boolean { return this.imageSphere != null && this.pendingImageSphere == null && this.transition == null && this.activeImage === this.requestedImage; }

	public load(resource: SurveyResource): ExternalImage | null {
		const image = this.createExternalImage(resource);
		if (!image) return null;

		const marker = this.createExternalImageMarker(image);
		this.markersGroup.add(marker);
		this.externalImageMarkers.push(marker);
		this.updateMarkerRadius(marker);
		this.externalImageMarkers.sort((first, second) =>
			this.getMarkerImage(first).name.localeCompare(this.getMarkerImage(second).name, undefined, { numeric: true }),
		);
		return image;
	}

	public getImages(): ExternalImage[] { return this.externalImageMarkers.map((marker) => this.getMarkerImage(marker)); }

	public getAdjacentImage(image: ExternalImage, direction: -1 | 1): ExternalImage | null {
		const images = this.getImages();
		const currentIndex = images.findIndex((entry) => entry.uuid === image.uuid);
		if (currentIndex < 0 || images.length < 2) return null;

		return images[(currentIndex + direction + images.length) % images.length];
	}

	public enter(image: ExternalImage, preserveWorldDirection?: THREE.Vector3, onLoadState?: (state: "loaded" | "error") => void): void {
		const viewer = this.options.viewer;
		if (!this.viewState) {
			this.viewState = {
				view: viewer.scene.view,
				fov: viewer.getFOV(),
				inputEnabled: viewer.inputHandler.enabled,
			};
			viewer.controls.stop();
			viewer.controls.stopTweens?.();
			// Comparison panes share a view; image mode needs its own camera state.
			viewer.scene.view = this.viewState.view.clone();
			// Potree's View.clone() does not copy position.
			viewer.scene.view.position.copy(this.viewState.view.position);
			viewer.inputHandler.enabled = false;
			this.updateOverlayRenderState(true);
		}
		this.magnifier.resetSnappedPoint();
		this.clearMarkerHover();
		this.markersGroup.visible = true;
		const previous = this.requestedImage ?? this.activeImage;
		if (previous && this.getAdjacentImage(previous, -1)?.uuid === image.uuid) this.navigationDirection = -1;
		else if (previous && this.getAdjacentImage(previous, 1)?.uuid === image.uuid) this.navigationDirection = 1;
		this.requestedImage = image;
		clearTimeout(this.prefetchTimer);
		this.prefetchUrls.clear();
		this.cancelQueuedTextures();
		const direction = preserveWorldDirection?.clone().normalize() ?? this.getDirection(image, 0, 0);
		this.showImage(image, direction, (state) => {
			if (state === "loaded") {
				this.activeImage = image;
				this.updateMarkerVisibility(image);
				// Avoid texture uploads competing with animation frames.
				if (!this.transition) this.prefetchAdjacentImages(image);
			}
			onLoadState?.(state);
		});
	}

	public updateView(image: ExternalImage, localYawDegrees: number, pitchDegrees: number, fovDegrees: number): void {
		if (!this.isReady) return;
		const viewer = this.options.viewer;
		viewer.scene.view.position.set(image.position.x, image.position.y, image.position.z);
		viewer.scene.view.direction = this.getDirection(image, localYawDegrees, pitchDegrees);
		viewer.scene.view.radius = 1;
		if (viewer.getFOV() !== fovDegrees) viewer.setFOV(fovDegrees);
	}

	public exit(): void {
		clearTimeout(this.prefetchTimer);
		this.clearImage();
		this.activeImage = null;
		this.requestedImage = null;
		this.prefetchUrls.clear();
		this.cancelQueuedTextures();
		if (!this.viewState) return;
		const viewer = this.options.viewer;
		viewer.scene.view = this.viewState.view;
		viewer.setFOV(this.viewState.fov);
		viewer.inputHandler.enabled = this.viewState.inputEnabled;
		this.viewState = null;
		this.magnifier.deactivate();
		this.updateOverlayRenderState(false);
		this.markersGroup.visible = this.markersVisible;
		this.updateMarkerVisibility(null);
	}

	public setVisible(visible: boolean): void {
		this.markersVisible = visible;
		if (!this.viewState) this.markersGroup.visible = visible;
	}

	public setImageOpacity(opacity: number): void {
		this.externalImageOpacity = opacity;
		if (this.transition) this.updateTransitionOpacity();
		else if (this.imageSphere) this.applyImageOpacity(this.imageSphere.material as THREE.MeshBasicMaterial);
	}

	public getImageOpacity(): number { return this.externalImageOpacity; }

	//Potree renders the point cloud and the external image sphere through its own passes that
	//don't necessarily respect this scene's renderOrder against them. Re-rendering the CAD
	//overlay in a genuinely separate, later render() call - hooked to "render.pass.end" - forces
	//it to always draw on top regardless of what those other passes did, instead of depending on
	//material/renderOrder tricks within a single render() call that Potree's own passes can undo.
	public renderOverlayOnTop = (): void => {
		if (!this.viewState) return;
		const viewer = this.options.viewer;
		const camera = viewer.scene.getActiveCamera();
		const renderer = viewer.renderer;
		const previousAutoClear = renderer.autoClear;
		try {
			renderer.autoClear = false;
			renderer.render(this.options.measurementsGroup, camera);
			renderer.render(this.options.unifiedLineMeasurementsGroup, camera);
			renderer.render(this.options.annotationsGroup, camera);
		} finally {
			renderer.autoClear = previousAutoClear;
		}
	};

	public getLocalOrientation(image: ExternalImage, worldDirection: THREE.Vector3): { yawDegrees: number; pitchDegrees: number } {
		const rotation = this.getImageRotation(image).clone().invert();
		const localDirection = worldDirection.clone().normalize().applyQuaternion(rotation);
		return {
			yawDegrees: THREE.MathUtils.radToDeg(Math.atan2(-localDirection.z, localDirection.x)),
			pitchDegrees: THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(localDirection.y, -1, 1))),
		};
	}

	public setInteractionElement(element: HTMLElement | null): void {
		this.interactionElement = element;
	}

	public isInteractionTarget(target: EventTarget | null): boolean {
		return !!target && !!this.interactionElement?.contains(target as Node);
	}

	private updateOverlayRenderState(enabled: boolean): void {
		for (const target of this.options.getOverlayTargets()) {
			this.applyOverlayRenderState(target, enabled);
		}
	}

	public updateOverlayRenderStateFor(target: any): void {
		this.applyOverlayRenderState(target, this.viewState != null);
	}

	private applyOverlayRenderState(target: any, enabled: boolean): void {
		target.traverse((object: any) => {
			const userData = object.userData || (object.userData = {});
			const material = object.material as THREE.Material | undefined;
			if (enabled) {
				if (!userData.externalOverlayOriginalState) {
					userData.externalOverlayOriginalState = {
						renderOrder: object.renderOrder ?? 0,
						material: material && {
							transparent: material.transparent,
							depthTest: material.depthTest,
							depthWrite: material.depthWrite,
						},
					};
				}
				object.renderOrder = OVERLAY_RENDER_ORDER;
				if (!material) return;
				material.transparent = true;
				material.depthTest = false;
				material.depthWrite = false;
				material.needsUpdate = true;
				return;
			}

			const originalState = userData.externalOverlayOriginalState;
			if (!originalState) return;
			object.renderOrder = originalState.renderOrder;
			if (material && originalState.material) {
				Object.assign(material, originalState.material);
				material.needsUpdate = true;
			}
			delete userData.externalOverlayOriginalState;
		});
	}

	public selectMarker(clientX: number, clientY: number, currentImage?: ExternalImage): ExternalImage | null {
		const marker = this.getMarkerAt(clientX, clientY, currentImage);
		return marker ? this.getMarkerImage(marker) : null;
	}

	public hoverMarker(clientX: number, clientY: number, currentImage?: ExternalImage): void {
		this.updateMarkerHover(this.getMarkerAt(clientX, clientY, currentImage));
	}

	public clearMarkerHover(): void { this.updateMarkerHover(null); }

	public updateMarkerHover(marker: THREE.Mesh | null): void {
		if (this.hoveredMarker === marker) return;
		if (this.hoveredMarker) {
			const previousMaterial = this.hoveredMarker.material as THREE.MeshBasicMaterial;
			previousMaterial.color.setHex(MARKER_DEFAULT_COLOR);
			previousMaterial.opacity = MARKER_DEFAULT_OPACITY;
			previousMaterial.needsUpdate = true;
		}

		this.hoveredMarker = marker;
		if (!marker) {
			this.hoverLabel.visible = false;
			return;
		}

		const material = marker.material as THREE.MeshBasicMaterial;
		material.color.setHex(MARKER_HOVER_COLOR);
		material.opacity = MARKER_DEFAULT_OPACITY;
		material.needsUpdate = true;
		const image = this.getMarkerImage(marker);
		this.hoverLabel.setText(image.name);
		this.hoverLabel.visible = true;
		this.updateHoverLabelPosition(marker);
	}

	public updateMarkerScales(camera: any, clientWidth: number, clientHeight: number): void {
		if (!this.hoveredMarker || !this.hoverLabel.visible) return;
		const marker = this.hoveredMarker;
		const distance = camera.position.distanceTo(marker.getWorldPosition(this.markerWorldPosition));
		const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
		if (Number.isFinite(pr) && pr > 0) this.hoverLabel.scale.setScalar(60 / pr);
		this.updateHoverLabelPosition(marker);
	}

	public cleanup(): void {
		this.exit();
		this.textureGeneration++;
		for (const entry of this.textureCache.values()) entry.texture.dispose();
		this.textureCache.clear();
		this.textureLoads.clear();
		this.updateMarkerHover(null);
		for (const marker of this.externalImageMarkers.splice(0)) {
			this.markersGroup.remove(marker);
			(marker.material as THREE.Material).dispose();
		}
	}

	private createExternalImageMarker(image: ExternalImage): THREE.Mesh {
		const marker = new THREE.Mesh(this.markerGeometry, new THREE.MeshBasicMaterial({
			color: MARKER_DEFAULT_COLOR,
			transparent: true,
			opacity: MARKER_DEFAULT_OPACITY,
			depthTest: false,
			depthWrite: false,
		}));
		marker.name = image.name;
		marker.position.set(image.position.x, image.position.y, image.position.z);
		marker.userData.externalImage = image;
		marker.renderOrder = 10000;

		return marker;
	}

	private updateMarkerRadius(newMarker: THREE.Mesh): void {
		let nearestDistance = Infinity;
		// Only distances involving the new center can change existing nearest neighbours.
		for (const marker of this.externalImageMarkers) {
			if (marker.position.equals(newMarker.position)) continue;
			const distance = marker.position.distanceTo(newMarker.position);
			nearestDistance = Math.min(nearestDistance, distance);
			this.markerNearestDistances.set(marker, Math.min(this.markerNearestDistances.get(marker) ?? Infinity, distance));
		}
		this.markerNearestDistances.set(newMarker, nearestDistance);

		let sum = 0;
		let count = 0;
		for (const marker of this.externalImageMarkers) {
			const distance = this.markerNearestDistances.get(marker) ?? Infinity;
			if (!Number.isFinite(distance)) continue;
			sum += distance;
			count++;
		}
		let radius = (sum / count) * 0.1 / 2;
		if (!Number.isFinite(radius)) radius = 0.125;
		radius = Math.max(radius, 0.02);
		for (const marker of this.externalImageMarkers) marker.scale.setScalar(radius);
		if (this.hoveredMarker) this.updateHoverLabelPosition(this.hoveredMarker);
	}

	private createExternalImage(resource: SurveyResource): ExternalImage | null {
		if (!resource.uuid || !resource.resourceUrl) return null;
		const metadata = resource.additionalData as ExternalImageMetadata | undefined;
		const center = metadata?.pose?.center;
		const rotation = metadata?.pose?.rotation;
		const position = { x: Number(center?.x), y: Number(center?.y), z: Number(center?.z) };
		const orientation = { x: Number(rotation?.x), y: Number(rotation?.y), z: Number(rotation?.z), w: Number(rotation?.w) };
		if (![position.x, position.y, position.z, orientation.x, orientation.y, orientation.z, orientation.w].every(Number.isFinite)) {
			return null;
		}

		const filename = typeof metadata?.filename === "string" ? metadata.filename : "";
		return {
			uuid: resource.uuid,
			name: filename ? filename.replace(/\.[^/.]+$/, "") : resource.name,
			imageUrl: resource.resourceUrl,
			position,
			rotation: orientation,
		};
	}

	private getImageRotation(image: ExternalImage): THREE.Quaternion {
		const cached = this.imageRotationCache.get(image);
		if (cached) return cached;

		const rotation = image.rotation;
		const q = new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w)
			.normalize()
			.invert()
			.multiply(EQUIRECTANGULAR_TO_CAMERA);
		this.imageRotationCache.set(image, q);
		return q;
	}

	private getDirection(image: ExternalImage, localYawDegrees: number, pitchDegrees: number): THREE.Vector3 {
		const localYaw = THREE.MathUtils.degToRad(localYawDegrees);
		const pitch = THREE.MathUtils.degToRad(pitchDegrees);
		const horizontalScale = Math.cos(pitch);
		return new THREE.Vector3(
			Math.cos(localYaw) * horizontalScale,
			Math.sin(pitch),
			-Math.sin(localYaw) * horizontalScale,
		).applyQuaternion(this.getImageRotation(image));
	}

	private showImage(image: ExternalImage, direction: THREE.Vector3, onLoadState?: (state: "loaded" | "error") => void): void {
		// Keep the current photograph and camera unchanged while the replacement loads.
		if (this.pendingImageSphere) this.disposeImageSphere(this.pendingImageSphere);
		const material = new THREE.MeshBasicMaterial({
			transparent: false,
			opacity: 1,
			alphaTest: 0,
			depthTest: false,
			depthWrite: false,
			side: THREE.BackSide,
		});
		const sphere = new THREE.Mesh(this.imageSphereGeometry, material);
		sphere.position.set(image.position.x, image.position.y, image.position.z);
		sphere.quaternion.copy(this.getImageRotation(image));
		sphere.renderOrder = 1000;
		sphere.visible = false;
		this.pendingImageSphere = sphere;
		this.getTexture(image).then(async (texture) => {
			// Finish the current glide before starting the newest requested destination.
			// Rapid navigation never snaps the camera or accumulates visible spheres.
			if (this.transition) await this.transition.finished;
			if (this.pendingImageSphere !== sphere) return;
			material.map = texture;
			this.applyImageOpacity(material);
			sphere.visible = true;
			this.imageViewGroup.add(sphere);
			const outgoing = this.imageSphere;
			this.imageSphere = sphere;
			this.pendingImageSphere = null;
			const view = this.options.viewer.scene.view;
			view.direction = direction;
			view.radius = 1;
			if (outgoing && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
				this.startTransition(outgoing, sphere, image);
			} else {
				if (outgoing) this.disposeImageSphere(outgoing);
				view.position.copy(sphere.position);
			}
			onLoadState?.("loaded");
		}, () => {
			if (this.pendingImageSphere !== sphere) return;
			this.disposeImageSphere(sphere);
			this.pendingImageSphere = null;
			onLoadState?.("error");
		});
	}

	private startTransition(outgoing: THREE.Mesh, incoming: THREE.Mesh, image: ExternalImage): void {
		const view = this.options.viewer.scene.view;
		const origin = view.position.clone();
		const destination = incoming.position.clone();
		const distance = origin.distanceTo(destination);
		const revealScene = distance > 0.001 && this.hasVisibleSpatialScene();
		const duration = revealScene
			? THREE.MathUtils.clamp(SPATIAL_GLIDE_MIN_DURATION + distance * 65, SPATIAL_GLIDE_MIN_DURATION, SPATIAL_GLIDE_MAX_DURATION)
			: THREE.MathUtils.clamp(GLIDE_MIN_DURATION + distance * 45, GLIDE_MIN_DURATION, GLIDE_MAX_DURATION);
		// With geometry, travel through the actual scene. Only use finite-radius
		// photo projection as a fallback for tours without visible 3D data.
		const projectionRadius = Math.max(revealScene ? IMAGE_SPHERE_RADIUS : 8, distance * 2);
		outgoing.scale.setScalar(projectionRadius / IMAGE_SPHERE_RADIUS);
		incoming.scale.copy(outgoing.scale);
		incoming.renderOrder = outgoing.renderOrder + 1;
		for (const sphere of [outgoing, incoming]) {
			const material = sphere.material as THREE.MeshBasicMaterial;
			material.transparent = true;
			material.needsUpdate = true;
		}
		let resolve: () => void;
		const finished = new Promise<void>((done) => { resolve = done; });
		const transition = { outgoing, progress: 0, revealScene, frame: 0, finished, resolve };
		this.transition = transition;
		this.updateTransitionOpacity();
		let started: number | undefined;
		const animate = (timestamp: number) => {
			if (this.transition !== transition) return;
			if (started === undefined) started = timestamp;
			const t = Math.min(1, (timestamp - started) / duration);
			// Smootherstep has zero velocity and acceleration at both ends.
			const eased = t * t * t * (t * (t * 6 - 15) + 10);
			view.position.lerpVectors(origin, destination, eased);
			transition.progress = revealScene ? t : eased;
			this.updateTransitionOpacity();
			if (t < 1) {
				transition.frame = requestAnimationFrame(animate);
				return;
			}
			this.disposeImageSphere(outgoing);
			incoming.scale.setScalar(1);
			incoming.renderOrder = 1000;
			this.applyImageOpacity(incoming.material as THREE.MeshBasicMaterial);
			this.transition = null;
			transition.resolve();
			this.trimTextureCache(image.imageUrl);
			if (this.requestedImage === image) this.prefetchAdjacentImages(image);
		};
		transition.frame = requestAnimationFrame(animate);
	}

	private hasVisibleSpatialScene(): boolean {
		const viewer = this.options.viewer;
		const isVisible = (object: THREE.Object3D): boolean => {
			for (let current: THREE.Object3D | null = object; current; current = current.parent) {
				if (!current.visible) return false;
			}
			return true;
		};
		return (viewer.scene.pointclouds ?? []).some(isVisible)
			|| (viewer.meshesGroup?.children ?? []).some(isVisible);
	}

	private updateTransitionOpacity(): void {
		if (!this.transition || !this.imageSphere) return;
		const { outgoing, progress, revealScene } = this.transition;
		if (revealScene) {
			// Brief handoffs at departure and arrival leave the middle of the
			// flight entirely unobstructed, exposing real point-cloud/mesh parallax.
			(outgoing.material as THREE.MeshBasicMaterial).opacity = this.externalImageOpacity
				* (1 - THREE.MathUtils.smootherstep(progress, 0, PANORAMA_HANDOFF_FRACTION));
			(this.imageSphere.material as THREE.MeshBasicMaterial).opacity = this.externalImageOpacity
				* THREE.MathUtils.smootherstep(progress, 1 - PANORAMA_HANDOFF_FRACTION, 1);
			return;
		}
		const incomingAlpha = this.externalImageOpacity * progress;
		(this.imageSphere.material as THREE.MeshBasicMaterial).opacity = incomingAlpha;
		// Compensate for source-over blending so total opacity stays constant,
		// including when the user has made the photos partially transparent.
		(outgoing.material as THREE.MeshBasicMaterial).opacity = incomingAlpha < 1
			? this.externalImageOpacity * (1 - progress) / (1 - incomingAlpha) : 0;
	}

	private getTexture(image: ExternalImage): Promise<THREE.Texture> {
		const url = image.imageUrl;
		const cached = this.textureCache.get(url);
		if (cached) {
			this.textureCache.delete(url);
			this.textureCache.set(url, cached);
			return Promise.resolve(cached.texture);
		}
		const pending = this.textureLoads.get(url);
		if (pending) return pending;
		const generation = this.textureGeneration;
		let start: () => void;
		let cancel: () => void;
		const load = new Promise<THREE.Texture>((resolve, reject) => {
			cancel = () => reject(new Error("Image loading cancelled"));
			start = () => {
				try {
					this.imageTextureLoader.load(url, (texture) => {
						if (generation !== this.textureGeneration
							|| (url !== this.requestedImage?.imageUrl && !this.prefetchUrls.has(url))) {
							texture.dispose();
							cancel();
							return;
						}
						const bytes = texture.image.width * texture.image.height * 4;
						if (url !== this.requestedImage?.imageUrl && !this.canPrefetch(url, bytes)) {
							this.prefetchUrls.delete(url);
							texture.dispose();
							cancel();
							return;
						}
						texture.encoding = THREE.LinearEncoding;
						texture.repeat.y = -1;
						texture.offset.y = 1;
						texture.generateMipmaps = false;
						texture.minFilter = THREE.LinearFilter;
						try {
							this.options.viewer.renderer.initTexture(texture);
							this.textureCache.set(url, { texture, bytes });
							this.trimTextureCache(url);
							resolve(texture);
						} catch (error) {
							texture.dispose();
							reject(error);
						}
					}, undefined, reject);
				} catch (error) {
					reject(error);
				}
			};
		});
		this.textureLoads.set(url, load);
		let started = false;
		this.textureQueue.push({ url, cancel, start: () => { started = true; start(); } });
		const finish = () => {
			if (this.textureLoads.get(url) === load) this.textureLoads.delete(url);
			if (started) this.runningTextureLoads--;
			this.startQueuedTextures();
		};
		load.then(finish, finish);
		this.startQueuedTextures();
		return load;
	}

	private cancelQueuedTextures(): void {
		for (const entry of this.textureQueue.splice(0)) {
			this.textureLoads.delete(entry.url);
			entry.cancel();
		}
	}

	private startQueuedTextures(): void {
		while (this.runningTextureLoads < MAX_TEXTURE_LOADS && this.textureQueue.length > 0) {
			const priority = this.textureQueue.findIndex((entry) => entry.url === this.requestedImage?.imageUrl);
			const [entry] = this.textureQueue.splice(Math.max(0, priority), 1);
			this.runningTextureLoads++;
			entry.start();
		}
	}

	private canPrefetch(url: string, estimatedBytes: number): boolean {
		let bytes = estimatedBytes;
		let count = 1;
		for (const [cachedUrl, entry] of this.textureCache) {
			if (cachedUrl !== url && (cachedUrl === this.activeImage?.imageUrl || this.prefetchUrls.has(cachedUrl))) {
				bytes += entry.bytes;
				count++;
			}
		}
		return bytes <= TEXTURE_CACHE_BYTES && count <= TEXTURE_CACHE_COUNT;
	}

	private trimTextureCache(loadedUrl: string): void {
		let bytes = [...this.textureCache.values()].reduce((sum, entry) => sum + entry.bytes, 0);
		for (const [url, entry] of this.textureCache) {
			if (this.textureCache.size <= TEXTURE_CACHE_COUNT && bytes <= TEXTURE_CACHE_BYTES) break;
			// Never dispose a displayed texture or the one about to replace it.
			if (url === this.activeImage?.imageUrl || url === this.requestedImage?.imageUrl || url === loadedUrl || this.prefetchUrls.has(url)) continue;
			if (entry.texture === (this.transition?.outgoing.material as THREE.MeshBasicMaterial | undefined)?.map) continue;
			this.textureCache.delete(url);
			entry.texture.dispose();
			bytes -= entry.bytes;
		}
	}

	private prefetchAdjacentImages(image: ExternalImage): void {
		clearTimeout(this.prefetchTimer);
		// Let the newly selected image render before warming the next pair.
		this.prefetchTimer = setTimeout(async () => {
			for (const direction of [this.navigationDirection, -this.navigationDirection] as (-1 | 1)[]) {
				if (!this.isActive || this.requestedImage !== image) return;
				const adjacent = this.getAdjacentImage(image, direction);
				if (!adjacent) continue;
				const estimatedBytes = this.textureCache.get(adjacent.imageUrl)?.bytes
					?? this.textureCache.get(image.imageUrl)?.bytes ?? TEXTURE_CACHE_BYTES;
				if (!this.canPrefetch(adjacent.imageUrl, estimatedBytes)) continue;
				this.prefetchUrls.add(adjacent.imageUrl);
				try {
					await this.getTexture(adjacent);
				} catch {
					// A speculative failure must not interrupt the current panorama.
				}
			}
		}, 50);
	}

	private clearImage(): void {
		if (this.transition) {
			cancelAnimationFrame(this.transition.frame);
			this.disposeImageSphere(this.transition.outgoing);
			this.transition.resolve();
			this.transition = null;
		}
		if (this.pendingImageSphere) this.disposeImageSphere(this.pendingImageSphere);
		this.pendingImageSphere = null;
		if (this.imageSphere) this.disposeImageSphere(this.imageSphere);
		this.imageSphere = null;
	}

	private disposeImageSphere(sphere: THREE.Mesh): void {
		this.imageViewGroup.remove(sphere);
		const material = sphere.material as THREE.MeshBasicMaterial;
		material.dispose();
	}

	private updateMarkerVisibility(activeImage: ExternalImage | null): void {
		if (activeImage && this.hoveredMarker && this.getMarkerImage(this.hoveredMarker).uuid === activeImage.uuid) {
			this.updateMarkerHover(null);
		}
		for (const marker of this.externalImageMarkers) {
			marker.visible = !activeImage || this.getMarkerImage(marker).uuid !== activeImage.uuid;
		}
	}

	private getMarkerAt(clientX: number, clientY: number, currentImage?: ExternalImage): THREE.Mesh | null {
		if (this.isActive && !this.isReady) return null;
		const viewer = this.options.viewer;
		const rect = viewer.renderer.domElement.getBoundingClientRect();
		this.markerRaycastMouse.set(
			((clientX - rect.left) / rect.width) * 2 - 1,
			-((clientY - rect.top) / rect.height) * 2 + 1,
		);
		this.markerRaycaster.setFromCamera(this.markerRaycastMouse, viewer.scene.getActiveCamera());
		for (const hit of this.markerRaycaster.intersectObjects(this.pickableMarkers, false)) {
			const image = this.getMarkerImage(hit.object as THREE.Mesh);
			if (image.uuid !== currentImage?.uuid) return hit.object as THREE.Mesh;
		}
		return null;
	}

	private updateHoverLabelPosition(marker: THREE.Mesh): void {
		this.hoverLabel.position.set(
			marker.position.x,
			marker.position.y,
			marker.position.z + Math.max(marker.scale.x * 3.0, 0.08),
		);
	}

	private getMarkerImage(marker: THREE.Mesh): ExternalImage { return marker.userData.externalImage as ExternalImage; }

	private applyImageOpacity(material: THREE.MeshBasicMaterial): void {
		material.transparent = this.externalImageOpacity < 1;
		material.opacity = this.externalImageOpacity;
		material.needsUpdate = true;
	}
}
